import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Stack,
  Chip,
  Button,
  List,
  ListItem,
  ListItemText,
  Paper,
  Grid,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PeopleIcon from '@mui/icons-material/People';
import { ExpandedRowContent } from './ExpandedRowContent';
import { RecipientsModal } from './RecipientsModal';
import { fetchBulkEmailDetails } from 'api/emailHistory/bulkEmailsApi';
import { FunnelChart, DeliveryStatusPieChart, TimeToOpenChart } from './charts';
import { useIsMobile } from 'hooks/useIsMobile';

/**
 * @component ExpandedRowBulkEmail
 * @description Expanded row content for bulk email details
 * @param {Object} props
 * @param {number} props.emailId - Bulk email ID
 * @param {function} props.onNavigateToIndividualEmails - Navigation handler
 * @returns {JSX.Element}
 */
export function ExpandedRowBulkEmail({ emailId, onNavigateToIndividualEmails }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recipientsModalOpen, setRecipientsModalOpen] = useState(false);

  useEffect(() => {
    const loadDetails = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await fetchBulkEmailDetails(emailId);
        setDetails(data);
      } catch (err) {
        console.error('Error fetching bulk email details:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadDetails();
  }, [emailId]);

  const topLinks = details?.topLinks || [];
  const timeToOpen = details?.timeToOpenDistribution || {};
  const hardBounced = details?.hardBounced || 0;
  const softBounced = details?.softBounced || 0;
  const isMobile = useIsMobile();

  return (
    <>
      <ExpandedRowContent
        loading={loading}
        error={error}
        isEmpty={!details}
        htmlContent={details?.emailContents}
        showEmailPreview={true}
      >
        {/* Charts in 2-column layout */}
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {/* Left Column: Funnel Chart */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                Bulk Send Performance
              </Typography>
              <FunnelChart
                sent={details?.attempted || 0}
                delivered={details?.delivered || 0}
                opened={details?.uniqueOpens || 0}
                clicked={details?.uniqueClicks || 0}
                loading={loading}
                height={200}
              />
            </Paper>
          </Grid>

          {/* Right Column: Delivery Status Pie Chart */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                Delivery Status
              </Typography>
              <DeliveryStatusPieChart
                delivered={details?.delivered || 0}
                bounced={hardBounced + softBounced}
                failed={0} // Failed count not available in API response
                loading={loading}
                width={300}
                height={200}
              />
              {/* Bounce Breakdown */}
              <Box sx={{ mt: 2 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {hardBounced > 0 && (
                    <Chip
                      label={`Hard: ${hardBounced}`}
                      size="small"
                      color="error"
                      variant="outlined"
                    />
                  )}
                  {softBounced > 0 && (
                    <Chip
                      label={`Soft: ${softBounced}`}
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  )}
                  {hardBounced === 0 && softBounced === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No bounces
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Stats and Top Links in 2-column layout */}
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {/* Left Column: Time to Open */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                Time to Open
              </Typography>
              <TimeToOpenChart
                timeToOpen={timeToOpen}
                loading={loading}
                height={200}
              />
            </Paper>
          </Grid>

          {/* Right Column: Top Clicked Links */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                Top Clicked Links
              </Typography>
              {topLinks.length > 0 ? (
                <List dense>
                  {topLinks.slice(0, 5).map((link, index) => (
                    <ListItem key={index} disablePadding>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{
                              wordBreak: 'break-all',
                              overflowWrap: 'break-word',
                              wordWrap: 'break-word',
                            }}
                          >
                            {link.url}
                          </Typography>
                        }
                        secondary={`${link.uniqueClicks} clicks`}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No link clicks recorded
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 1 : 0,
            justifyContent: isMobile ? 'stretch' : 'space-between', 
            mt: 3 
          }}
        >
          <Button
            variant="outlined"
            startIcon={<PeopleIcon />}
            onClick={() => setRecipientsModalOpen(true)}
            fullWidth={isMobile}
          >
            View Recipients
          </Button>
          <Button
            variant="outlined"
            endIcon={<ArrowForwardIcon />}
            onClick={() => onNavigateToIndividualEmails(emailId)}
            fullWidth={isMobile}
          >
            View Individual Emails Sent
          </Button>
        </Box>
      </ExpandedRowContent>

      {/* Recipients Modal */}
      <RecipientsModal
        open={recipientsModalOpen}
        onClose={() => setRecipientsModalOpen(false)}
        bulkEmailId={emailId}
      />
    </>
  );
}

